import { useState, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';

const WHEEL_ITEMS = [
  // Classics
  'Negroni', 'Sex on the Beach', 'Strawberry Daiquiri', 'Cosmopolitan', 'Clover Club',
  'Cuba Libre', 'Caipirinha', 'Margarita', 'Tequila Sunrise', 'Moscow Mule',
  'Moscow Mule Raspberry', 'Moscow Mule Strawberry',
  // Hip & Trendy
  'Pornstar Martini', 'Espresso Martini', 'Lazy Red Cheeks', 'Bring me back to Ibiza',
  'Golden Sunset', 'Thank God it\'s Friday',
  // Creamy
  'Mango Coconut', 'Coco Passion Bonita', 'Pi\u00f1a Colada',
  // Heavy
  'Whiskey Sour', 'Long Island Iced Tea', 'Gin Fizz', 'Hurricane',
  // Mojito
  'Mojito', 'Mango Mojito', 'Raspberry Mojito', 'Passion Mojito', 'Strawberry Mojito',
  // Mocktails
  'Coconut Mango Kiss', 'Fruit Punch', 'Berry Beach', 'Save Sex',
  'Virgin Mojito', 'Virgin Mango Mojito', 'Virgin Raspberry Mojito',
  'Virgin Passion Mojito', 'Virgin Strawberry Mojito',
  'Strawberry Passion Fizz', 'Tropical Mango Cooler', 'Lazy Red Cheeks NA', 'Apple Basil Smash NA',
  // Our Spritzes
  '"BarBaaR" Spritz', 'Aperol Spritz', 'Martini Bianco Spritz', 'Saint Germain Spritz',
  'Limoncello Spritz', 'Limoncello Raspberry Spritz', 'Raspberry Spritz',
  'Spicy Mango Spritz', 'Apple Ginger Spritz NA', 'Martini Bianco Spritz NA',
];

const GOLD = '#C8A46A';
const DARK = '#0A0A0A';
const COLORS = ['#C8A46A', '#1a1a1a', '#2a2a2a', '#C8A46A', '#1a1a1a', '#2a2a2a'];

function drawWheel(canvas, items, rotation) {
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(cx, cy) - 8;
  const arc = (2 * Math.PI) / items.length;

  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  items.forEach(function (item, i) {
    const angle = i * arc;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r, angle, angle + arc);
    ctx.closePath();
    ctx.fillStyle = COLORS[i % COLORS.length];
    ctx.fill();
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.save();
    ctx.rotate(angle + arc / 2);
    ctx.textAlign = 'right';
    ctx.fillStyle = i % 3 === 0 ? DARK : '#EDEDED';
    const fontSize = items.length > 40 ? 7 : items.length > 20 ? 9 : 11;
    ctx.font = `bold ${fontSize}px Montserrat, sans-serif`;
    ctx.fillText(item, r - 14, 4);
    ctx.restore();
  });

  ctx.restore();

  // pointer triangle
  ctx.beginPath();
  ctx.moveTo(cx + r + 2, cy);
  ctx.lineTo(cx + r - 22, cy - 12);
  ctx.lineTo(cx + r - 22, cy + 12);
  ctx.closePath();
  ctx.fillStyle = GOLD;
  ctx.fill();
  ctx.strokeStyle = DARK;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function getWinnerIndex(rotation, count) {
  const arc = (2 * Math.PI) / count;
  const normalized = ((2 * Math.PI) - (rotation % (2 * Math.PI))) % (2 * Math.PI);
  return Math.floor(normalized / arc) % count;
}

function fireConfetti() {
  const duration = 3000;
  const end = Date.now() + duration;
  const colors = ['#C8A46A', '#EDEDED', '#FFD700', '#FF6B6B', '#4ECDC4'];

  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 }, colors: colors });
    confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors: colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  confetti({ particleCount: 100, spread: 120, origin: { y: 0.6 }, colors: colors });
  setTimeout(function () {
    confetti({ particleCount: 80, spread: 100, origin: { y: 0.5, x: 0.5 }, colors: colors, startVelocity: 45 });
  }, 500);
}

export default function SpinWheel() {
  const canvasRef = useRef(null);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const rotationRef = useRef(0);
  const animRef = useRef(null);

  const draw = useCallback(function (rot) {
    const canvas = canvasRef.current;
    if (canvas) drawWheel(canvas, WHEEL_ITEMS, rot);
  }, []);

  const handleSpin = useCallback(function () {
    if (spinning) return;
    setSpinning(true);
    setWinner(null);

    const totalRotation = (Math.random() * 5 + 8) * 2 * Math.PI;
    const targetRotation = rotationRef.current + totalRotation;
    const duration = 8000 + Math.random() * 4000;
    const start = performance.now();
    const startRotation = rotationRef.current;

    function animate(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = startRotation + totalRotation * eased;
      rotationRef.current = current;
      draw(current);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        rotationRef.current = targetRotation;
        draw(targetRotation);
        const idx = getWinnerIndex(targetRotation, WHEEL_ITEMS.length);
        setWinner(WHEEL_ITEMS[idx]);
        setSpinning(false);
        fireConfetti();
      }
    }

    animRef.current = requestAnimationFrame(animate);
  }, [spinning, draw]);

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-24 flex flex-col items-center" data-testid="spin-wheel-page">
      <div className="text-center mb-6 px-4">
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#EDEDED] tracking-wide" style={{ fontFamily: "'Cinzel', serif" }}>
          <span className="text-4xl sm:text-5xl md:text-6xl">B</span>AR
          <span className="text-4xl sm:text-5xl md:text-6xl">B</span>AA
          <span className="text-4xl sm:text-5xl md:text-6xl">R</span>
        </h1>
        <p className="font-body text-sm tracking-[0.2em] text-[#C8A46A] uppercase mt-2">Keuzestress? Spin the Wheel</p>
      </div>

      <div className="relative" style={{ width: 'min(90vw, 520px)', height: 'min(90vw, 520px)' }}>
        <canvas
          ref={canvasRef}
          width={520}
          height={520}
          className="w-full h-full"
          style={{ display: 'block' }}
        />
        {/* draw initial */}
        {canvasRef.current === null && <InitDraw canvasRef={canvasRef} draw={draw} />}

        <button
          onClick={handleSpin}
          disabled={spinning}
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-[#C8A46A] flex items-center justify-center transition-all duration-300 ${
            spinning
              ? 'bg-[#C8A46A]/20 cursor-not-allowed'
              : 'bg-[#0A0A0A] hover:bg-[#C8A46A] hover:text-[#0A0A0A] cursor-pointer'
          }`}
          data-testid="spin-button"
        >
          <span className={`font-body text-[10px] md:text-xs tracking-wider uppercase text-center leading-tight font-bold ${spinning ? 'text-[#C8A46A]/50' : 'text-[#C8A46A] hover:text-[#0A0A0A]'}`}>
            {spinning ? 'Spinning...' : 'Make a\nChoice'}
          </span>
        </button>
      </div>

      {winner && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={function () { setWinner(null); }}
          data-testid="winner-overlay"
        >
          <div
            className="text-center p-10 md:p-14 border border-[#C8A46A]/40 bg-[#0A0A0A]/95 backdrop-blur-md max-w-md mx-4"
            onClick={function (e) { e.stopPropagation(); }}
            data-testid="winner-popup"
          >
            <p className="font-body text-sm tracking-[0.2em] text-[#C8A46A] uppercase mb-4">Your drink is</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#EDEDED] mb-6" style={{ fontFamily: "'Cinzel', serif" }}>
              {winner}
            </h2>
            <p className="font-body text-[#A1A1A1] text-sm mb-8">Order at our waiters!</p>
            <button
              onClick={function () { setWinner(null); }}
              className="px-8 py-3 bg-[#C8A46A] text-[#0A0A0A] font-body text-sm tracking-[0.15em] uppercase hover:bg-[#b8944a] transition-all duration-300"
              data-testid="winner-close-btn"
            >
              Cheers!
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

function InitDraw({ canvasRef, draw }) {
  setTimeout(function () { draw(0); }, 50);
  return null;
}
